import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { useToast } from '../context/ToastContext';
import { useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Calendar, MapPin, Users, CheckCircle, XCircle, AlertCircle, Clock } from 'lucide-react';
import { formatMatchDateFull } from '../utils/dateFormatting';
import axios from 'axios';

interface Match {
  id: number;
  opponent_name: string;
  match_date: string;
  location?: string;
  formatted_address?: string;
  latitude?: number;
  longitude?: number;
  match_type?: string;
  home_away?: string;
  notes?: string;
  team_id: number;
  team?: {
    id: number;
    name: string;
  };
}

interface PlayerAvailability {
  id: number;
  match_id: number;
  player_id: number;
  availability_status: 'available' | 'unavailable' | 'maybe';
  notes?: string;
  updated_at: string;
}

const AvailabilityPage: React.FC = () => {
  const { user } = useAuth();
  const { teams, loading: teamLoading } = useTeam();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [allMatches, setAllMatches] = useState<Match[]>([]);
  const [availability, setAvailability] = useState<Map<number, PlayerAvailability>>(new Map());
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  // Track recently updated matches with timestamps to keep them visible temporarily
  const [recentlyUpdatedMatches, setRecentlyUpdatedMatches] = useState<Map<number, number>>(new Map());
  // Track matches that are collapsing (for smooth exit animation)
  const [collapsingMatches, setCollapsingMatches] = useState<Set<number>>(new Set());
  
  // Ref to track match card elements for scroll preservation
  const matchCardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Ref to track card heights for collapse animation
  const cardHeightsRef = useRef<Map<number, number>>(new Map());
  
  // Ref to store scroll position before state updates
  const scrollPositionRef = useRef<number | null>(null);
  const scrollMatchIdRef = useRef<number | null>(null);
  
  // Restore scroll position after DOM updates
  useLayoutEffect(() => {
    if (scrollPositionRef.current !== null && scrollMatchIdRef.current !== null) {
      const matchCard = matchCardRefs.current.get(scrollMatchIdRef.current);
      if (matchCard) {
        // Calculate where the card is now
        const currentCardTop = matchCard.getBoundingClientRect().top + window.scrollY;
        // Calculate where it was before
        const previousCardTop = scrollPositionRef.current;
        // Adjust scroll to maintain relative position
        const scrollDiff = currentCardTop - previousCardTop;
        window.scrollTo(0, window.scrollY + scrollDiff);
      }
      // Reset refs
      scrollPositionRef.current = null;
      scrollMatchIdRef.current = null;
    }
  });
  
  // Initialize filters from URL params if present
  const getInitialMatchFilter = (): 'all' | 'upcoming' | 'past' => {
    const matchParam = searchParams.get('match');
    if (matchParam && ['all', 'upcoming', 'past'].includes(matchParam)) {
      return matchParam as 'all' | 'upcoming' | 'past';
    }
    return 'upcoming';
  };

  const getInitialTeamFilter = (): number | 'all' => {
    const teamParam = searchParams.get('team');
    if (teamParam) {
      const teamId = parseInt(teamParam, 10);
      if (!isNaN(teamId)) {
        return teamId;
      }
    }
    return 'all';
  };

  const getInitialStatusFilter = (): 'all' | 'available' | 'unavailable' | 'maybe' | 'not_set' => {
    const statusParam = searchParams.get('status');
    if (statusParam && ['available', 'unavailable', 'maybe', 'not_set'].includes(statusParam)) {
      return statusParam as 'all' | 'available' | 'unavailable' | 'maybe' | 'not_set';
    }
    return 'all';
  };
  
  // Filters
  const [matchFilter, setMatchFilter] = useState<'all' | 'upcoming' | 'past'>(getInitialMatchFilter());
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<number | 'all'>(getInitialTeamFilter());
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'unavailable' | 'maybe' | 'not_set'>(getInitialStatusFilter());

  // Helper function to check if a match is in the past
  const isMatchInPast = (matchDate: string): boolean => {
    return new Date(matchDate) < new Date();
  };

  // Fetch matches and availability
  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setAllMatches([]);
        setAvailability(new Map());
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch all matches for the user across all teams
        const matchesResponse = await axios.get(`/matches/users/${user.id}/matches`);
        const matchesData = matchesResponse.data;
        
        // Sort matches by date (upcoming first)
        matchesData.sort((a: Match, b: Match) => 
          new Date(a.match_date).getTime() - new Date(b.match_date).getTime()
        );
        
        setAllMatches(matchesData);
        
        // Fetch all availability for the user
        const availabilityResponse = await axios.get(`/availability/users/${user.id}/availability`);
        const availabilityData = availabilityResponse.data;
        
        // Create availability map
        const availabilityMap = new Map<number, PlayerAvailability>();
        availabilityData.forEach((avail: PlayerAvailability) => {
          availabilityMap.set(avail.match_id, avail);
        });
        
        setAvailability(availabilityMap);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load matches. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (!teamLoading && user) {
      fetchData();
    }
  }, [teamLoading, user, toast]);

  // Cleanup timer for recently updated matches - mark as collapsing only if card doesn't match filter
  useEffect(() => {
    if (recentlyUpdatedMatches.size === 0) return;

    const timeoutIds: NodeJS.Timeout[] = [];
    const now = Date.now();
    const VISIBILITY_DURATION = 2500; // 2.5 seconds
    const COLLAPSE_DURATION = 1500; // Animation duration in ms (1.5 seconds)

    recentlyUpdatedMatches.forEach((timestamp, matchId) => {
      const elapsed = now - timestamp;
      const remaining = Math.max(0, VISIBILITY_DURATION - elapsed);
      
      // Check if card should collapse or just be removed from recently updated
      const cleanupTimeoutId = setTimeout(() => {
        // Always collapse unless statusFilter is 'all'
        const shouldCollapse = statusFilter !== 'all';

        if (shouldCollapse) {
          // Store the actual height before collapsing
          const cardElement = matchCardRefs.current.get(matchId);
          if (cardElement) {
            const height = cardElement.offsetHeight;
            cardHeightsRef.current.set(matchId, height);
            
            // Set initial max-height to current height
            cardElement.style.maxHeight = `${height}px`;
            
            // Then animate to 0 in the next frame
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (cardElement) {
                  cardElement.style.maxHeight = '0';
                }
              });
            });
          }
          
          setCollapsingMatches(prev => new Set(prev).add(matchId));
          
          // Then remove from collapsing set after animation completes
          setTimeout(() => {
            setCollapsingMatches(prev => {
              const newSet = new Set(prev);
              newSet.delete(matchId);
              return newSet;
            });
            cardHeightsRef.current.delete(matchId);
            if (cardElement) {
              cardElement.style.maxHeight = '';
            }
          }, COLLAPSE_DURATION);
        }
        
        // Always remove from recently updated matches
        setRecentlyUpdatedMatches(prev => {
          const newMap = new Map(prev);
          newMap.delete(matchId);
          return newMap;
        });
      }, remaining);
      
      timeoutIds.push(cleanupTimeoutId);
    });

    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [recentlyUpdatedMatches, statusFilter]);

  // Get availability status for a match
  const getAvailabilityStatus = useCallback((matchId: number): 'available' | 'unavailable' | 'maybe' | null => {
    const avail = availability.get(matchId);
    return avail ? avail.availability_status : null;
  }, [availability]);

  // Filter matches by team, date, and status using useMemo for efficient filtering
  const matches = useMemo(() => {
    let filtered = allMatches;
    
    // Filter by team
    if (selectedTeamFilter !== 'all') {
      filtered = filtered.filter(match => match.team_id === selectedTeamFilter);
    }
    
    // Filter by date
    if (matchFilter === 'upcoming') {
      filtered = filtered.filter(match => !isMatchInPast(match.match_date));
    } else if (matchFilter === 'past') {
      filtered = filtered.filter(match => isMatchInPast(match.match_date));
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(match => {
        // Include recently updated matches even if they don't match the status filter
        if (recentlyUpdatedMatches.has(match.id)) {
          return true;
        }
        // Include collapsing matches so they can animate out
        if (collapsingMatches.has(match.id)) {
          return true;
        }
        const status = getAvailabilityStatus(match.id);
        if (statusFilter === 'not_set') {
          return status === null;
        }
        return status === statusFilter;
      });
    }
    
    return filtered;
  }, [allMatches, selectedTeamFilter, matchFilter, statusFilter, getAvailabilityStatus, recentlyUpdatedMatches, collapsingMatches]);

  // Handle availability change for a single match
  const handleAvailabilityChange = async (
    matchId: number,
    status: 'available' | 'unavailable' | 'maybe',
    event?: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (!user) return;
    
    // Prevent default button behavior and blur to prevent focus-related scrolling
    if (event) {
      event.preventDefault();
      event.currentTarget.blur();
    }
    
    // Get the match card element to maintain scroll position relative to it (for future scroll restoration)
    matchCardRefs.current.get(matchId);
    
    const currentStatus = getAvailabilityStatus(matchId);
    
    // If clicking the same status, toggle it off (unset)
    if (currentStatus === status) {
      const match = allMatches.find(m => m.id === matchId);
      if (match) {
        await handleClearAvailability(matchId, match.team_id, event);
      }
      return;
    }
    
    // Optimistically update the state immediately
    const optimisticAvailability: PlayerAvailability = {
      id: availability.get(matchId)?.id || 0,
      match_id: matchId,
      player_id: user.id,
      availability_status: status,
      notes: '',
      updated_at: new Date().toISOString()
    };
    
    setAvailability(prev => {
      const newMap = new Map(prev);
      newMap.set(matchId, optimisticAvailability);
      return newMap;
    });
    
    try {
      setUpdating(matchId);
      
      // Use the response from POST directly instead of refetching
      const response = await axios.post(`/availability/matches/${matchId}/availability`, {
        match_id: matchId,
        player_id: user.id,
        availability_status: status,
        notes: ''
      });
      
      // Update with the actual response from the server
      const updatedAvailability = response.data;
      setAvailability(prev => {
        const newMap = new Map(prev);
        newMap.set(matchId, updatedAvailability);
        return newMap;
      });
      
      // Add to recently updated matches to keep it visible temporarily
      setRecentlyUpdatedMatches(prev => {
        const newMap = new Map(prev);
        newMap.set(matchId, Date.now());
        return newMap;
      });
      
      // Scroll restoration is handled by useLayoutEffect
    } catch (error) {
      console.error('Failed to set availability:', error);
      
      // Revert optimistic update on error
      setAvailability(prev => {
        const newMap = new Map(prev);
        const original = availability.get(matchId);
        if (original) {
          newMap.set(matchId, original);
        } else {
          newMap.delete(matchId);
        }
        return newMap;
      });
      
      toast.error('Failed to update availability. Please try again.');
      // Scroll restoration is handled by useLayoutEffect
    } finally {
      setUpdating(null);
    }
  };

  // Handle clearing availability for a match
  const handleClearAvailability = async (matchId: number, teamId: number, event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!user) return;
    
    // Prevent default button behavior and blur to prevent focus-related scrolling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
    }
    
    // Store scroll position relative to the match card before state update
    const matchCard = matchCardRefs.current.get(matchId);
    if (matchCard) {
      scrollPositionRef.current = matchCard.getBoundingClientRect().top + window.scrollY;
      scrollMatchIdRef.current = matchId;
    }
    
    // Store original value for potential rollback
    const originalAvailability = availability.get(matchId);
    
    // Optimistically remove from availability map immediately
    setAvailability(prev => {
      const newMap = new Map(prev);
      newMap.delete(matchId);
      return newMap;
    });
    
    try {
      setUpdating(matchId);
      
      await axios.delete(`/teams/${teamId}/availability/${matchId}/${user.id}`);
      
      // Add to recently updated matches to keep it visible temporarily and show "Saved" overlay
      setRecentlyUpdatedMatches(prev => {
        const newMap = new Map(prev);
        newMap.set(matchId, Date.now());
        return newMap;
      });
      
      // Scroll restoration is handled by useLayoutEffect
    } catch (error) {
      console.error('Failed to clear availability:', error);
      
      // Revert optimistic update on error
      if (originalAvailability) {
        setAvailability(prev => {
          const newMap = new Map(prev);
          newMap.set(matchId, originalAvailability);
          return newMap;
        });
      }
      
      toast.error('Failed to clear availability. Please try again.');
      // Scroll restoration is handled by useLayoutEffect
    } finally {
      setUpdating(null);
    }
  };

  // Match card skeleton component
  const MatchCardSkeleton = () => (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 dark:border-gray-700/50 animate-pulse">
      <div className="flex flex-col gap-4">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2 gap-2">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
          </div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-56"></div>
          </div>
        </div>
        <div className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-700/50 pt-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </div>
      </div>
    </div>
  );

  if (teamLoading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div>
          {/* Header and Filters */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">My Availability</h1>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-2">
              {teams.length > 0 && (
                <select
                  id="team-filter"
                  value={selectedTeamFilter}
                  onChange={(e) => setSelectedTeamFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  className="w-full sm:w-auto px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px]"
                >
                  <option value="all">All Teams</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              )}
              <select
                id="match-filter"
                value={matchFilter}
                onChange={(e) => setMatchFilter(e.target.value as 'all' | 'upcoming' | 'past')}
                className="w-full sm:w-auto px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] min-w-[180px]"
              >
                <option value="all">All Matches</option>
                <option value="upcoming">Upcoming Matches</option>
                <option value="past">Past Matches</option>
              </select>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'available' | 'unavailable' | 'maybe' | 'not_set')}
                className="w-full sm:w-auto px-3 py-2.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] min-w-[180px]"
              >
                <option value="all">All Statuses</option>
                <option value="available">Available</option>
                <option value="unavailable">Unavailable</option>
                <option value="maybe">If Needed</option>
                <option value="not_set">Not Set</option>
              </select>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mb-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {matches.length} of {allMatches.length} matches
            </p>
          </div>

          {/* Matches List */}
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <MatchCardSkeleton key={i} />
              ))}
            </div>
          ) : matches.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 md:p-10 text-center shadow-sm border border-gray-100 dark:border-gray-700/50">
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-6 h-6 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {matchFilter === 'upcoming' && 'No upcoming matches'}
                {matchFilter === 'past' && 'No past matches'}
                {matchFilter === 'all' && 'No matches found'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {matches.map((match) => {
                const status = getAvailabilityStatus(match.id);
                const isUpdatingThis = updating === match.id;
                const isPast = isMatchInPast(match.match_date);
                const isRecentlyUpdated = recentlyUpdatedMatches.has(match.id);
                const isCollapsing = collapsingMatches.has(match.id);
                
                return (
                  <div 
                    key={match.id}
                    ref={(el) => {
                      if (el) {
                        matchCardRefs.current.set(match.id, el);
                      } else {
                        matchCardRefs.current.delete(match.id);
                      }
                    }}
                    className={`relative rounded-xl transition-[max-height,opacity,margin,padding] duration-[1500ms] overflow-hidden ${
                      isCollapsing
                        ? 'opacity-0 mb-0'
                        : 'p-4 md:p-6'
                    } ${
                      isPast
                        ? 'bg-gray-50 dark:bg-gray-800/50 opacity-60 shadow-sm border border-gray-100 dark:border-gray-700/50'
                        : isRecentlyUpdated
                        ? 'bg-white dark:bg-gray-800 border-2 border-green-400 dark:border-green-500 ring-2 ring-green-200 dark:ring-green-800/50 shadow-sm'
                        : 'bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700/50 hover:shadow-md hover:border-gray-200 dark:hover:border-gray-600'
                    }`}
                    style={isCollapsing ? { 
                      marginBottom: '0',
                      paddingTop: '0',
                      paddingBottom: '0',
                      paddingLeft: '0',
                      paddingRight: '0'
                    } : undefined}
                  >
                    {/* Saved Overlay */}
                    {isRecentlyUpdated && (
                      <div className="absolute inset-0 bg-green-500/90 dark:bg-green-600/90 backdrop-blur-sm rounded-xl flex items-center justify-center z-10 animate-in fade-in duration-300">
                        <div className="flex flex-col items-center gap-2">
                          <CheckCircle className="w-8 h-8 text-white" />
                          <span className="text-white font-semibold text-lg">Saved</span>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-col gap-4">
                      {/* Match Info */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2 gap-2">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <h3 className={`text-base md:text-lg font-medium ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                              {match.home_away === 'away' ? 'at' : 'vs'} {match.opponent_name}
                            </h3>
                            {isPast && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex-shrink-0">
                                Past
                              </span>
                            )}
                          </div>
                          {status === null && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 flex-shrink-0">
                              Not Set
                            </span>
                          )}
                        </div>
                        {match.team && (
                          <p className={`text-sm mb-2 flex items-center gap-2 ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            <Users className="w-4 h-4 flex-shrink-0" />
                            {match.team.name}
                          </p>
                        )}
                        <div className="space-y-1">
                          {(() => {
                            const formatted = formatMatchDateFull(match.match_date);
                            const textClass = isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400';
                            return (
                              <>
                                <p className={`text-sm flex items-center gap-2 ${textClass}`}>
                                  <Calendar className="w-4 h-4 flex-shrink-0" />
                                  {formatted.date}
                                </p>
                                <p className={`text-sm flex items-center gap-2 ${textClass}`}>
                                  <Clock className="w-4 h-4 flex-shrink-0" />
                                  {formatted.time}
                                </p>
                              </>
                            );
                          })()}
                          {(match.location || match.formatted_address) && (
                            <p className={`text-sm flex items-center gap-2 ${isPast ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400'}`}>
                              <MapPin className="w-4 h-4 flex-shrink-0" />
                              {match.formatted_address || match.location}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Availability Status & Actions */}
                      {!isPast && (
                        <div className="flex flex-col gap-3 border-t border-gray-100 dark:border-gray-700/50 pt-3">
                          {/* Action Buttons */}
                          <div className="grid grid-cols-3 gap-2">
                            {(() => {
                              const isSelected = status === 'available';
                              const hasSelection = status !== null;
                              return (
                                <button
                                  onClick={(e) => handleAvailabilityChange(match.id, 'available', e)}
                                  disabled={isUpdatingThis}
                                  className={`px-2.5 md:px-4 py-3 md:py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-1.5 md:gap-2 whitespace-nowrap ${
                                    isSelected
                                      ? 'bg-green-600 text-white shadow-md ring-2 ring-green-300'
                                      : hasSelection
                                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                                  }`}
                                >
                                  <CheckCircle className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-green-600 dark:text-green-500'}`} />
                                  {isUpdatingThis ? 'Updating...' : 'Available'}
                                </button>
                              );
                            })()}
                            {(() => {
                              const isSelected = status === 'maybe';
                              const hasSelection = status !== null;
                              return (
                                <button
                                  onClick={(e) => handleAvailabilityChange(match.id, 'maybe', e)}
                                  disabled={isUpdatingThis}
                                  className={`px-2.5 md:px-4 py-3 md:py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-1.5 md:gap-2 whitespace-nowrap ${
                                    isSelected
                                      ? 'bg-yellow-600 text-white shadow-md ring-2 ring-yellow-300'
                                      : hasSelection
                                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                                      : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                                  }`}
                                >
                                  <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-yellow-600 dark:text-yellow-500'}`} />
                                  {isUpdatingThis ? 'Updating...' : 'If Needed'}
                                </button>
                              );
                            })()}
                            {(() => {
                              const isSelected = status === 'unavailable';
                              const hasSelection = status !== null;
                              return (
                                <button
                                  onClick={(e) => handleAvailabilityChange(match.id, 'unavailable', e)}
                                  disabled={isUpdatingThis}
                                  className={`px-2.5 md:px-4 py-3 md:py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] flex items-center justify-center gap-1.5 md:gap-2 whitespace-nowrap ${
                                    isSelected
                                      ? 'bg-red-600 text-white shadow-md ring-2 ring-red-300'
                                      : hasSelection
                                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-600 opacity-50'
                                      : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50'
                                  }`}
                                >
                                  <XCircle className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-white' : hasSelection ? 'text-gray-400 dark:text-gray-500' : 'text-red-600 dark:text-red-500'}`} />
                                  {isUpdatingThis ? 'Updating...' : 'Unavailable'}
                                </button>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityPage;

